import type { ProviderToken } from '@angular/core';
import { ChangeDetectorRef, Component, Injector, Input } from '@angular/core';
import type { FormControl } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import type { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs';

import type { SelectGroupedOptions } from '@recruitee/user-interface';
import { SelectComponent } from '@recruitee/user-interface';
import { LifeCycle } from '@recruitee/utils';

import { CurrencySelectOptionsFacade } from './data/currency-select-options.facade';
import { DepartmentSelectOptionsFacade } from './data/department-select-options.facade';
import type {
  DynamicSelectFieldType,
  DynamicSelectOptionsProvider,
} from './dynamic-select-field.types';

// TODO: We should extract this from requisitions. It should probably be split into separate and independent select fields:
// DepartmentSelect (move to Department lib), CurrencySelect (move to Currency lib) etc.

const injectorMap: Record<DynamicSelectFieldType, ProviderToken<DynamicSelectOptionsProvider>> = {
  department: DepartmentSelectOptionsFacade,
  currency: CurrencySelectOptionsFacade,
};

@Component({
  selector: 'rt-requisitions-dynamic-select-field',
  template: `
    <rt-select
      [formControl]="form"
      [hasInputTrigger]="true"
      [searchPlaceholder]="searchPlaceholder"
      [groupedOptions]="selectGroupedOptions"
      [hasClearButton]="hasClearButton"
      [disabled]="isDisabled"
      [readOnly]="isReadOnly"
      [placeholder]="placeholder"
      [isInvalid]="isInvalid"
    ></rt-select>
  `,
  imports: [SelectComponent, ReactiveFormsModule],
  providers: [DepartmentSelectOptionsFacade, CurrencySelectOptionsFacade],
})
export class DynamicSelectFieldComponent {
  @Input() public type: DynamicSelectFieldType;
  @Input() public form: FormControl;
  @Input() public searchPlaceholder: string;
  @Input() public placeholder: string;
  @Input() public isDisabled: boolean = false;
  @Input() public isReadOnly: boolean = false;
  @Input() public hasClearButton: boolean = false;
  @Input() public isInvalid: boolean = false;

  public selectGroupedOptions$: Observable<SelectGroupedOptions[]>;
  public selectGroupedOptions: SelectGroupedOptions[] = [];

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;

  constructor(
    private injector: Injector,
    protected cdRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.selectGroupedOptions$ = this.injector.get(injectorMap[this.type]).selectGroupedOptions$;

    this.selectGroupedOptions$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(options => {
      this.selectGroupedOptions = options;
      this.cdRef.detectChanges();
    });
  }
}
